import React, { FC, useMemo, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import moment from 'moment-timezone';
import { toast } from 'react-toastify';

import { IAbsenceRequestDTO } from 'src/api/dtos';
import { Modal, Popper, Status, TrashIcon } from 'src/shared/components';
import { DAY_COMMA_DATE_FORMAT } from 'src/shared/constants';
import { NotificationFlagsEnums } from 'src/shared/enums';
import { useThunkDispatch } from 'src/shared/hooks';
import { useTranslate } from 'src/shared/providers';
import { isUserAllowedToCancelAbsence, mapAbsenceStatusStyle } from 'src/shared/utils';
import { cancelAbsence } from 'src/store/absence/absences.actions';

import { CalendarNotesInfo, CalendarTimeInfo } from './modal-content';

interface Props {
  employeeId: number | undefined;
  item: IAbsenceRequestDTO;
  open: boolean;
  onClose: () => void;
}

export const CalendarAbsenceRequestModal: FC<Props> = ({ employeeId, item, open, onClose }) => {
  const { t } = useTranslate();
  const dispatch = useThunkDispatch();
  const [cancelConfirmEl, setCancelConfirmEl] = useState<HTMLElement | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const { absenceRequestId, startDate, endDate, endTime, startTime, statusId, absenceTypeName, notes } = item;
  const formattedStartDate = moment(startDate).format(DAY_COMMA_DATE_FORMAT);
  const formattedEndDate = moment(endDate).format(DAY_COMMA_DATE_FORMAT);

  const title =
    statusId === NotificationFlagsEnums.REJECTED
      ? t('calendar.rejectedAbsenceRequest')
      : t('calendar.pendingAbsenceRequest');

  const allowCancelling = useMemo(
    () =>
      isUserAllowedToCancelAbsence({
        statusId: item.statusId,
        startDate: item.startDate,
        endDate: item.endDate,
        isPartialDay: item.isPartialDay,
        allowCancelAfterApproved: false,
      }),
    [item],
  );

  const handleStatusCancel = async () => {
    try {
      if (!allowCancelling) {
        return;
      }

      setCancelConfirmEl(null);
      setLoading(true);
      await dispatch(cancelAbsence({ notificationIds: [item.notificationId], params: {} }));
      onClose();
    } catch {
      toast.info(t('global.oopsAnErrorOccurredWhileProcessingYourRequest'), { type: toast.TYPE.ERROR });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      type={mapAbsenceStatusStyle[statusId]}
      open={open}
      onClose={onClose}
      modalHeaderProps={{
        title: `${absenceTypeName} - ${title}`,
        message:
          formattedStartDate === formattedEndDate ? formattedStartDate : `${formattedStartDate} - ${formattedEndDate}`,
        startIcon: <Status size="large" variant={mapAbsenceStatusStyle[statusId]} />,
        onClose,
      }}
    >
      <div className="ms-3 ps-3">
        {startTime && endTime && <CalendarTimeInfo startTime={startTime} endTime={endTime} />}
        {!!notes.length && (
          <CalendarNotesInfo
            notes={notes.map((note) => {
              return {
                label: note.employeeId === employeeId ? t('absences.note') : t('absences.approverNote'),
                value: note.content,
              };
            })}
          />
        )}
        {allowCancelling && (
          <div className="mt-4 d-flex justify-content-end">
            <Popper
              id={absenceRequestId}
              anchorEl={cancelConfirmEl}
              submitText={t('global.yesImSure')}
              closeText={t('global.no')}
              onSubmit={handleStatusCancel}
              onClose={() => setCancelConfirmEl(null)}
            >
              <p className="small">{t('absences.areYouSureYouWantToCancelThisRequest')}</p>
            </Popper>
            <LoadingButton
              aria-describedby={absenceRequestId}
              color="primary"
              variant="contained"
              loadingPosition="start"
              loading={loading}
              disabled={loading}
              startIcon={<TrashIcon />}
              onClick={(e) => setCancelConfirmEl(cancelConfirmEl ? null : e.currentTarget)}
            >
              {t('calendar.cancelRequest')}
            </LoadingButton>
          </div>
        )}
      </div>
    </Modal>
  );
};
